import { nextTick } from "vue";
import type { Ref } from "vue";
import type { CreateDeliveryRequestMutation } from "@/generated/graphql";
import shopStore from "@/store/shopStore";
import type { TagPrintData, SlipPrintData } from "../types";

/**
 * 荷札/伝票印刷プレビュー作成に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterPrintParams {
  createDeliveryRequestMutation: Ref<CreateDeliveryRequestMutation | null>;
  deliveryRequestCreateInput: any;
  shopData: Ref<{ name: string; shippingLabelPrintFlg: number; deliveryOrderPrintFlg: number } | null>;
  tagPrintShippingData: Ref<TagPrintData[]>;
  slipPrintShippingData: Ref<SlipPrintData[]>;
  confirmTagPrintDialog: Ref<boolean>;
  confirmSlipPrintDialog: Ref<boolean>;
  tagLabelPrintDialogRef: Ref<any>;
  slipLabelPrintDialogRef: Ref<any>;
  wordList: Ref<Record<string, string>>;
}

/**
 * 登録完了後の印刷データ作成とプレビュー表示を扱う composable。
 */
export const useDeliveryRequestRegisterPrint = ({
  createDeliveryRequestMutation,
  deliveryRequestCreateInput,
  shopData,
  tagPrintShippingData,
  slipPrintShippingData,
  confirmTagPrintDialog,
  confirmSlipPrintDialog,
  tagLabelPrintDialogRef,
  slipLabelPrintDialogRef,
  wordList,
}: UseDeliveryRequestRegisterPrintParams) => {
  const onClickTagPrintPreview = async (): Promise<void> => {
    tagPrintShippingData.value = [
      {
        receptionNo: createDeliveryRequestMutation.value?.createDeliveryRequest
          ?.reception_no
          ? createDeliveryRequestMutation.value.createDeliveryRequest.reception_no.substring(
              createDeliveryRequestMutation.value.createDeliveryRequest
                .reception_no.length - 5
            )
          : "",
        nameKana: createDeliveryRequestMutation.value?.createDeliveryRequest
          ?.delivery_name_kana1
          ? createDeliveryRequestMutation.value.createDeliveryRequest
              .delivery_name_kana1 +
            (createDeliveryRequestMutation.value.createDeliveryRequest
              ?.delivery_name_kana2
              ? createDeliveryRequestMutation.value.createDeliveryRequest
                  .delivery_name_kana2
              : "")
          : " ",
        unit: createDeliveryRequestMutation.value?.createDeliveryRequest?.unit
          ? createDeliveryRequestMutation.value.createDeliveryRequest.unit
          : 0,
        shopName: shopData.value!.name ? shopData.value!.name : " ",
        areaName: " ",
        receptionDate: createDeliveryRequestMutation.value?.createDeliveryRequest
          ?.created_date
          ? createDeliveryRequestMutation.value.createDeliveryRequest.created_date
          : "0000-00-00 00:00:00",
      },
    ];

    onClickCheckTagPrint();

    nextTick(() => {
      const tagLabelPrintDialog = tagLabelPrintDialogRef.value;
      const content = tagLabelPrintDialog?.printableContent?.innerHTML;
      if (content) {
        tagLabelPrintDialog.showPrintTab(content);
      }
      confirmTagPrintDialog.value = false;
    });
  };

  const onClickSlipPrintPreview = async (): Promise<void> => {
    slipPrintShippingData.value = [
      {
        receptionNo: createDeliveryRequestMutation.value?.createDeliveryRequest
          ?.reception_no
          ? createDeliveryRequestMutation.value.createDeliveryRequest.reception_no
          : "",
        receptionTimedelivery: createDeliveryRequestMutation.value
          ?.createDeliveryRequest?.created_date
          ? createDeliveryRequestMutation.value.createDeliveryRequest.created_date
          : "",
        clientName: formatClientName(),
        deliveryAddress: formatDeliveryAddress(),
        clientTel: deliveryRequestCreateInput.delivery_phone
          ? deliveryRequestCreateInput.delivery_phone
          : "",
        deliveryEstimatedDate: createDeliveryRequestMutation.value
          ?.createDeliveryRequest?.required_delivery_date
          ? createDeliveryRequestMutation.value.createDeliveryRequest
              .required_delivery_date
          : "",
        deliveryId: formatDeliveryId(),
        plannedDeliveryTime: formatPlannedDeliveryTime(),
        unit: createDeliveryRequestMutation.value?.createDeliveryRequest?.unit
          ? createDeliveryRequestMutation.value.createDeliveryRequest.unit
          : 0,
        shopName: shopData.value!.name ? shopData.value!.name : " ",
        memoDelivery: deliveryRequestCreateInput.delivery_notes
          ? deliveryRequestCreateInput.delivery_notes
          : "",
        shopAddress: shopStore.getShopAddress,
        lblTransportationCost: wordList.value!.lbl_transportation_cost
          ? wordList.value!.lbl_transportation_cost
          : " ",
      },
    ];

    onClickCheckSlipPrint();

    nextTick(() => {
      const slipLabelPrintDialog = slipLabelPrintDialogRef.value;
      const content = slipLabelPrintDialog?.printableContent?.innerHTML;
      if (content) {
        slipLabelPrintDialog.showPrintTab(content);
      }
      confirmSlipPrintDialog.value = false;
    });
  };

  const formatClientName = (): string => {
    const name1 = deliveryRequestCreateInput.delivery_name1;
    const name2 = deliveryRequestCreateInput.delivery_name2;
    const nameKana1 = deliveryRequestCreateInput.delivery_name_kana1;
    const nameKana2 = deliveryRequestCreateInput.delivery_name_kana2;
    return `${name1 ? name1 : ""} ${name2 ? name2 : ""} 様(${nameKana1 ? nameKana1 : ""}${nameKana2 ? nameKana2 : ""})`.trim();
  };

  const formatDeliveryAddress = (): string => {
    const zipCode = deliveryRequestCreateInput.delivery_zip_code;
    const address1 = deliveryRequestCreateInput.delivery_address1;
    const address2 = deliveryRequestCreateInput.delivery_address2;
    const address3 = deliveryRequestCreateInput.delivery_address3;
    return `${zipCode ? "〒" + zipCode.substring(0, 3) + "-" + zipCode.substring(3) : ""} ${address1 ? address1 : ""} ${address2 ? address2 : ""} ${address3 ? address3 : ""}`.trim();
  };

  const formatDeliveryId = (): string => {
    const deliveryId = createDeliveryRequestMutation.value?.createDeliveryRequest?.delivery_id;
    if (deliveryId === 0) {
      return "都度便";
    } else if (deliveryId) {
      return `${deliveryId}便`;
    } else {
      return "";
    }
  };

  const formatPlannedDeliveryTime = (): string => {
    if (
      createDeliveryRequestMutation.value!.createDeliveryRequest!.delivery_start_time &&
      createDeliveryRequestMutation.value!.createDeliveryRequest!.delivery_end_time
    ) {
      return `${formatTime(createDeliveryRequestMutation.value!.createDeliveryRequest!.delivery_start_time)}～${formatTime(createDeliveryRequestMutation.value!.createDeliveryRequest!.delivery_end_time)}`;
    } else {
      return "";
    }
  };

  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5);
  };

  const onClickCheckTagPrint = (): void => {
    confirmTagPrintDialog.value = true;
  };

  const onClickCheckSlipPrint = (): void => {
    confirmSlipPrintDialog.value = true;
  };

  return {
    onClickTagPrintPreview,
    onClickSlipPrintPreview,
    formatClientName,
    formatDeliveryAddress,
    formatDeliveryId,
    formatPlannedDeliveryTime,
    formatTime,
    onClickCheckTagPrint,
    onClickCheckSlipPrint,
  };
};
